/**
 * Tracks which services the user has already purchased in this browser session.
 * Used to show dynamic bundle pricing (only charge for remaining services).
 */

export type ServiceId = "color-analysis" | "kibbe" | "draping" | "outfits";

const STORAGE_KEY = "ownedServices";

// Individual service prices for calculating total value
export const SERVICE_PRICES: Record<ServiceId, number> = {
  "color-analysis": 19.99, // Includes draping bundled
  "kibbe": 14.99,
  "draping": 0, // Now bundled with color-analysis
  "outfits": 14.99,
};

// Total value if all services purchased separately
export const TOTAL_INDIVIDUAL_VALUE = Object.values(SERVICE_PRICES).reduce((a, b) => a + b, 0); // $58.97

/**
 * Get all services the user has already purchased
 */
export function getOwnedServices(): ServiceId[] {
  if (typeof window === "undefined") return [];

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Mark a service as owned/purchased
 */
export function markServiceOwned(serviceId: ServiceId): void {
  if (typeof window === "undefined") return;

  const owned = getOwnedServices();
  if (!owned.includes(serviceId)) {
    owned.push(serviceId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(owned));
  }
}

/**
 * Mark multiple services as owned
 */
export function markServicesOwned(serviceIds: ServiceId[]): void {
  serviceIds.forEach(markServiceOwned);
}

/**
 * Check if a specific service is owned
 */
export function isServiceOwned(serviceId: ServiceId): boolean {
  return getOwnedServices().includes(serviceId);
}

/**
 * Get services the user does NOT yet own
 */
export function getRemainingServices(): ServiceId[] {
  const allServices: ServiceId[] = ["color-analysis", "kibbe", "draping", "outfits"];
  const owned = getOwnedServices();
  return allServices.filter(s => !owned.includes(s));
}

/**
 * Get the number of remaining services
 */
export function getRemainingCount(): number {
  return getRemainingServices().length;
}

/**
 * Calculate the value of remaining services if purchased individually
 */
export function getRemainingIndividualValue(): number {
  return getRemainingServices().reduce((total, serviceId) => total + SERVICE_PRICES[serviceId], 0);
}

/**
 * Clear all owned services (for testing)
 */
export function clearOwnedServices(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
}
